import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Rocket, FlaskConical, AlertTriangle, ExternalLink, ShieldCheck, Zap, Globe, MessageCircle, Twitter, ArrowUpRight } from 'lucide-react';

export default function LaunchpadCoin() {
  const { coinId } = useParams<{ coinId: string }>();

  // Sample data for the 'alien' coin
  const coinData = {
    id: 'alien',
    name: 'Alien Rizz',
    ticker: 'ALRIZZ',
    status: 'Live',
    marketCap: '$42,069',
    holders: 1337,
    image: '/launchpad/assets/alien/logo.png',
    description: 'The first coin from the BrainRotBlast Labs. Transparent tokenomics for maximum aura.',
    contract: 'ALRiZz1234567890abcdefghijklmnopqrstuvwxyz',
    tokenomics: {
      total: '1,000,000,000',
      liquidity: '80%',
      team: '10%',
      marketing: '10%'
    },
    socials: {
      website: 'https://brainrotblast.com/launchpad/alien',
      twitter: 'https://twitter.com/brainrotblast',
      telegram: 'https://t.me/brainrotblast'
    }
  };

  if (coinId !== 'alien') {
    return (
      <div className="py-40 text-center font-bungee opacity-20 text-3xl uppercase tracking-widest border-4 border-dashed border-emerald-800 rounded-[3rem] bg-emerald-900/20 shadow-inner">
        Coin Not Found in Labs
      </div>
    );
  }

  return (
    <div className="space-y-12 animate-in slide-in-from-bottom-8 duration-700">
      <div className="flex flex-col lg:flex-row gap-12">
        {/* Left: Coin Info Card */}
        <div className="lg:w-1/3 space-y-8">
          <div className="bg-zinc-900/80 border-4 border-emerald-500/10 rounded-[3rem] p-8 space-y-8 shadow-2xl relative overflow-hidden">
            <div className="aspect-square bg-zinc-800 rounded-[2rem] overflow-hidden border-4 border-emerald-500/20">
              <img src={coinData.image} alt={coinData.name} className="w-full h-full object-cover" />
            </div>
            <div className="space-y-2">
              <h2 className="text-5xl font-bungee text-white uppercase tracking-tighter leading-none">{coinData.name}</h2>
              <span className="text-emerald-400 font-bungee text-xl">${coinData.ticker}</span>
            </div>
            <div className="flex items-center gap-4">
              <a href={coinData.socials.twitter} target="_blank" rel="noopener noreferrer" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl text-zinc-400 hover:text-emerald-400 transition-all"><Twitter size={20} /></a>
              <a href={coinData.socials.telegram} target="_blank" rel="noopener noreferrer" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl text-zinc-400 hover:text-emerald-400 transition-all"><MessageCircle size={20} /></a>
              <a href={coinData.socials.website} target="_blank" rel="noopener noreferrer" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl text-zinc-400 hover:text-emerald-400 transition-all"><Globe size={20} /></a>
            </div>
            <div className="pt-6 border-t border-emerald-500/10 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Market Cap</span>
                <span className="font-bungee text-emerald-400">{coinData.marketCap}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Holders</span>
                <span className="font-bungee text-white">{coinData.holders.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="p-8 bg-emerald-500/5 border-2 border-emerald-500/20 rounded-[2.5rem] space-y-4">
            <div className="flex items-center gap-3 text-emerald-400">
              <ShieldCheck size={20} />
              <h4 className="font-bungee text-sm uppercase">Contract Verified</h4>
            </div>
            <p className="text-[10px] text-zinc-500 font-mono break-all bg-black/40 p-3 rounded-lg border border-emerald-500/10">
              {coinData.contract}
            </p>
          </div>
        </div>

        {/* Right: Detailed Info */}
        <div className="lg:w-2/3 space-y-12">
          <section className="bg-zinc-900/50 border-2 border-emerald-500/5 rounded-[3rem] p-10 space-y-6">
            <h3 className="text-3xl font-bungee text-white uppercase tracking-tight">Description</h3>
            <p className="text-zinc-400 font-bungee text-sm leading-relaxed">
              {coinData.description}
            </p>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-zinc-900/50 border-2 border-emerald-500/5 rounded-[3rem] p-10 space-y-8">
              <h3 className="text-2xl font-bungee text-white uppercase tracking-tight">Tokenomics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Total Supply</span>
                  <span className="font-bungee text-white">{coinData.tokenomics.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Liquidity</span>
                  <span className="font-bungee text-emerald-400">{coinData.tokenomics.liquidity}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Team</span>
                  <span className="font-bungee text-emerald-400">{coinData.tokenomics.team}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Marketing</span>
                  <span className="font-bungee text-emerald-400">{coinData.tokenomics.marketing}</span>
                </div>
              </div>
            </div>

            <div className="bg-emerald-500/5 border-2 border-emerald-500/20 rounded-[3rem] p-10 space-y-8">
              <h3 className="text-2xl font-bungee text-emerald-400 uppercase tracking-tight">Labs Check</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-xs font-bungee text-emerald-100/80">
                  <Zap size={16} className="text-emerald-400" />
                  <span>Authority Revoked</span>
                </div>
                <div className="flex items-center gap-3 text-xs font-bungee text-emerald-100/80">
                  <Zap size={16} className="text-emerald-400" />
                  <span>Liquidity Locked</span>
                </div>
                <div className="flex items-center gap-3 text-xs font-bungee text-emerald-100/80">
                  <Zap size={16} className="text-emerald-400" />
                  <span>Public Founder Wallets</span>
                </div>
              </div>
              <Link to="/launchpad/policy" className="inline-flex items-center gap-2 text-emerald-400 font-bungee text-[10px] uppercase hover:underline">
                View Sell Policy <ArrowUpRight size={12} />
              </Link>
            </div>
          </section>

          <section className="bg-zinc-900/50 border-2 border-emerald-500/5 rounded-[3rem] p-10 text-center space-y-6">
            <h3 className="text-3xl font-bungee text-white uppercase tracking-tight">Trade ALRIZZ</h3>
            <p className="text-zinc-500 font-bungee text-xs uppercase tracking-widest">Available on Solana DEXs</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="px-8 py-4 bg-emerald-500 text-black rounded-2xl font-bungee text-sm uppercase shadow-lg hover:scale-105 transition-all flex items-center gap-2">
                Trade on Raydium <ExternalLink size={16} />
              </button>
              <button className="px-8 py-4 bg-zinc-800 text-white rounded-2xl font-bungee text-sm uppercase shadow-lg hover:scale-105 transition-all flex items-center gap-2">
                View on DexScreener <ExternalLink size={16} />
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
